import type { ReactNode } from 'react';

export interface ContentArticleImage {
  id: string;
  src: string;
  alt?: string;
}

interface Props {
  body: string;
  images?: ContentArticleImage[];
  className?: string;
}

function safeLink(value: string) {
  const url = value.trim();
  return /^(https?:\/\/|mailto:|\/)/i.test(url) ? url : '';
}

function imageSource(value: string, images: ContentArticleImage[]) {
  const url = value.trim();
  if (url.startsWith('content-image://')) {
    return images.find((image) => image.id === url.slice('content-image://'.length))?.src || '';
  }
  return /^(https?:\/\/|data:image\/(?:png|jpe?g|webp|gif);base64,)/i.test(url) ? url : '';
}

function inlineContent(text: string, images: ContentArticleImage[], keyPrefix: string): ReactNode[] {
  const pattern = /(!\[[^\]]*]\([^)]+\)|\[[^\]]+]\([^)]+\)|\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let index = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) nodes.push(text.slice(cursor, start));
    const token = match[0];
    const key = `${keyPrefix}-${index++}`;
    const image = token.match(/^!\[([^\]]*)]\(([^)]+)\)$/);
    const link = token.match(/^\[([^\]]+)]\(([^)]+)\)$/);
    if (image) {
      const src = imageSource(image[2], images);
      nodes.push(src ? <img key={key} src={src} alt={image[1] || '教程图片'} loading="lazy" /> : image[1]);
    } else if (link) {
      const href = safeLink(link[2]);
      nodes.push(href
        ? <a key={key} href={href} target={href.startsWith('/') ? undefined : '_blank'} rel={href.startsWith('/') ? undefined : 'noreferrer'}>{link[1]}</a>
        : link[1]);
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    cursor = start + token.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

export function contentArticleImages(metadata: Record<string, unknown> | undefined): ContentArticleImage[] {
  const source = metadata?.contentImages;
  if (!Array.isArray(source)) return [];
  return source.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const value = item as Record<string, unknown>;
    const id = String(value.id || '');
    const src = String(value.src || '');
    if (!id || !src) return [];
    return [{ id, src, alt: String(value.alt || '') }];
  });
}

export function firstContentArticleImage(body: string, images: ContentArticleImage[]) {
  const match = body.match(/!\[[^\]]*]\(content-image:\/\/([^)]+)\)/);
  return match ? images.find((image) => image.id === match[1])?.src || '' : '';
}

export function MarkdownArticle({ body, images = [], className = '' }: Props) {
  const lines = body.replace(/\r/g, '').split('\n');
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const raw = lines[index];
    const line = raw.trim();
    if (!line) {
      index += 1;
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      const level = Math.min(4, heading[1].length + 1);
      const Heading = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5';
      blocks.push(<Heading key={`heading-${index}`}>{inlineContent(heading[2], images, `heading-${index}`)}</Heading>);
      index += 1;
      continue;
    }
    if (/^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const ordered = /^\d+\.\s+/.test(line);
      const items: ReactNode[] = [];
      while (index < lines.length) {
        const itemLine = lines[index].trim();
        const itemMatch = ordered ? itemLine.match(/^\d+\.\s+(.+)$/) : itemLine.match(/^[-*]\s+(.+)$/);
        if (!itemMatch) break;
        items.push(<li key={`item-${index}`}>{inlineContent(itemMatch[1], images, `item-${index}`)}</li>);
        index += 1;
      }
      blocks.push(ordered ? <ol key={`list-${index}`}>{items}</ol> : <ul key={`list-${index}`}>{items}</ul>);
      continue;
    }
    const quote = line.match(/^>\s*(.+)$/);
    if (quote) {
      blocks.push(<blockquote key={`quote-${index}`}>{inlineContent(quote[1], images, `quote-${index}`)}</blockquote>);
      index += 1;
      continue;
    }
    if (/^---+$/.test(line)) {
      blocks.push(<hr key={`rule-${index}`} />);
      index += 1;
      continue;
    }
    const paragraphLines = [line];
    index += 1;
    while (index < lines.length) {
      const next = lines[index].trim();
      if (!next || /^(#{1,4})\s+|^[-*]\s+|^\d+\.\s+|^>\s*|^---+$/.test(next)) break;
      paragraphLines.push(next);
      index += 1;
    }
    const paragraph = paragraphLines.join(' ');
    blocks.push(<p key={`paragraph-${index}`}>{inlineContent(paragraph, images, `paragraph-${index}`)}</p>);
  }

  return <div className={`markdown-article ${className}`.trim()}>{blocks}</div>;
}
